import { Alert, Button, Card, Space, Tag, Timeline, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DollarOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  UserAddOutlined,
  AlertOutlined,
  ExclamationCircleOutlined,
  CommentOutlined,
  CreditCardOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  SyncOutlined,
  LineChartOutlined,
  HolderOutlined,
} from '@ant-design/icons'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'
import type { Layout, Layouts } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface AdminAnnouncement {
  id: number
  title: string
  body?: string
  severity: 'info' | 'warning' | 'critical' | 'success'
}

interface ReportsOverview {
  mrr: string
  active_subscriptions: number
  open_invoices: number
  open_invoices_total: string
  overdue_invoices: number
  overdue_invoices_total: string
  income_current_month: string
  income_last_month: string
  new_customers_this_month: number
  churn_rate_last_30_days: string
}

interface DunningBucket { level: number; count: number; sum: string; action?: string; days_after_due?: number }
interface BillingOverview {
  due_count: number; due_sum: string
  overdue_count: number; overdue_sum: string
  dunning: DunningBucket[]
}

interface LicenseInfo {
  valid: boolean
  type?: string
  product?: string
  grace_period?: boolean
}

interface InvoiceRow {
  id: number
  invoice_no: string
  customer_id: number
  recipient_name?: string
  recipient_company?: string
  status: string
  currency: string
  total_gross: string
  due_date?: string
  issue_date?: string
  created_at: string
}

interface CreditLimitRow {
  customer_id: number
  customer_no: string
  customer_name: string
  currency: string
  balance: string
  credit_limit: string
  excess_amount: string
}

interface TicketRow {
  id: number
  ticket_no: string
  subject: string
  status: string
  priority: string
  customer_id?: number
  customer_name?: string
  updated_at: string
}

interface OrderRow {
  id: number
  order_no?: string
  customer_id?: number
  customer_name?: string
  customer_email?: string
  status: string
  total_gross: string
  currency: string
  placed_at?: string
}

interface ProvJobRow {
  id: number
  subscription_id?: number
  adapter: string
  action: string
  status: string
  attempts: number
  max_attempts: number
  last_error?: string
  created_at: string
}

interface PSPBalance { provider: string; available: string; pending: string; currency: string; test_mode: boolean }
interface PSPBalances {
  stripe: PSPBalance | null
  paypal: PSPBalance | null
  stripe_error?: string
  paypal_error?: string
  test_mode?: boolean
}
interface SalesRow { month: string; income: string; tax: string; invoices: number }
interface MRRRow   { month: string; mrr: string }

interface DueSubRow {
  id: number
  customer_id: number
  customer_name: string
  customer_no: string
  product_name: string
  billing_cycle: string
  currency: string
  next_due_date?: string | null
}

interface AuditRow {
  id: number
  actor: string
  action: string
  subject?: string | null
  subject_kind?: string | null
  ip?: string | null
  created_at: string
}

function fmtMonth(m: string) {
  const [y, mo] = m.split('-')
  const names = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez']
  return `${names[parseInt(mo, 10) - 1]} ${y.slice(2)}`
}

function fmtEur(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`
  return v.toFixed(0)
}

type Delta = { dir: 'up' | 'down' | 'flat'; text: string }
function pctDelta(cur: number, prev: number): Delta | undefined {
  if (!isFinite(cur) || !isFinite(prev) || prev === 0) return undefined
  const d = ((cur - prev) / prev) * 100
  if (Math.abs(d) < 0.05) return { dir: 'flat', text: '0,0 %' }
  return { dir: d > 0 ? 'up' : 'down', text: `${d > 0 ? '+' : ''}${d.toFixed(1).replace('.', ',')} %` }
}

// Premium KPI card: soft accent icon badge, big tabular value, optional
// trend chip. `accent` drives the icon badge + delta colour theme.
function KpiCard({
  icon, label, value, sub, accent = '#6366F1', delta, deltaGood = 'up', onClick, title,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  accent?: string
  delta?: Delta
  deltaGood?: 'up' | 'down'
  onClick?: () => void
  title?: string
}) {
  // Colour the delta chip by whether the movement is "good" for this metric.
  const good = delta && (delta.dir === deltaGood)
  const deltaCls = delta?.dir === 'flat' ? 'flat' : good ? 'good' : 'bad'
  return (
    <div
      className={`kpi-card${onClick ? ' kpi-card--link' : ''}`}
      style={{ ['--kpi-accent' as string]: accent }}
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
    >
      <div className="kpi-card-top">
        <span className="kpi-card-icon">{icon}</span>
        {delta && (
          <span className={`kpi-card-delta ${deltaCls}`}>
            {delta.dir === 'up' ? '▲' : delta.dir === 'down' ? '▼' : '▬'} {delta.text}
          </span>
        )}
      </div>
      <div className="kpi-card-value">{value}</div>
      <div className="kpi-card-label">{label}</div>
      {sub && <div className="kpi-card-sub">{sub}</div>}
    </div>
  )
}

function ChartEmpty({ loading }: { loading?: boolean }) {
  return (
    <div className="chart-empty">
      {loading ? <SyncOutlined spin /> : <LineChartOutlined />}
      <span>{loading ? 'Lädt…' : 'Noch keine Daten für diesen Zeitraum'}</span>
    </div>
  )
}

interface SMHost {
  id: number; name: string; address: string; status: string
  updates_available: number; security_updates: number; reboot_required: boolean
}
interface SMDomain { id: number; name: string; registrar: string; expires_at?: string; auto_renew: boolean }
interface ServerMgmt {
  reboot_required: SMHost[]; updates_pending: SMHost[]; problem: SMHost[]; expiring_domains: SMDomain[]
  totals: { hosts: number; reboot: number; updates: number; security: number; problem: number; domains_expiring: number }
}

// Fully configurable dashboard grid (drag + resize, 2D). Layout persists per
// admin user via /me/prefs/dashboard_grid_v1. KPI strip + announcements stay fixed.
const ResponsiveGrid = WidthProvider(Responsive)
const DASH_COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }
const DASH_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }
// Default lg layout — a sensible starting grid; the user drags/resizes from here.
const DASH_DEFAULT_LG: Layout[] = [
  { i: 'servermgmt', x: 0, y: 0, w: 12, h: 7, minW: 4, minH: 4 },
  { i: 'billing', x: 0, y: 7, w: 4, h: 6, minW: 3, minH: 4 },
  { i: 'revenue', x: 4, y: 7, w: 4, h: 7, minW: 3, minH: 4 },
  { i: 'mrr', x: 8, y: 7, w: 4, h: 7, minW: 3, minH: 4 },
  { i: 'openinvoices', x: 0, y: 14, w: 6, h: 9, minW: 3, minH: 4 },
  { i: 'opentickets', x: 6, y: 14, w: 6, h: 9, minW: 3, minH: 4 },
  { i: 'orders', x: 0, y: 23, w: 6, h: 8, minW: 3, minH: 4 },
  { i: 'failedjobs', x: 6, y: 23, w: 6, h: 8, minW: 3, minH: 4 },
  { i: 'renewals', x: 0, y: 31, w: 12, h: 8, minW: 4, minH: 4 },
  { i: 'overdue', x: 0, y: 39, w: 6, h: 7, minW: 3, minH: 4 },
  { i: 'creditlimit', x: 6, y: 39, w: 6, h: 7, minW: 3, minH: 4 },
  { i: 'activity', x: 0, y: 46, w: 12, h: 7, minW: 4, minH: 4 },
]

export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [dashLayouts, setDashLayouts] = useState<Layouts>({ lg: DASH_DEFAULT_LG })
  const [dashEdit, setDashEdit] = useState(false)
  const dashLayoutQ = useQuery({
    queryKey: ['dash-grid'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { value: Layouts | null } }>('/api/v1/me/prefs/dashboard_grid_v1')
      return r.data.data?.value ?? null
    },
    staleTime: Infinity,
  })
  useEffect(() => {
    const saved = dashLayoutQ.data
    if (saved && typeof saved === 'object' && !Array.isArray(saved) && Object.keys(saved).length) {
      setDashLayouts(saved)
    }
  }, [dashLayoutQ.data])
  const onDashLayoutChange = (_cur: Layout[], all: Layouts) => {
    if (dashEdit) setDashLayouts(all)
  }
  const saveDashLayout = () => {
    apiClient.put('/api/v1/me/prefs/dashboard_grid_v1', dashLayouts).catch(() => {})
    setDashEdit(false)
  }
  const resetDashLayout = () => {
    const def: Layouts = { lg: DASH_DEFAULT_LG }
    setDashLayouts(def)
    apiClient.put('/api/v1/me/prefs/dashboard_grid_v1', def).catch(() => {})
  }

  const serverMgmtQ = useQuery({
    queryKey: ['dash-server-mgmt'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: ServerMgmt }>('/api/v1/reports/server-management')
      return r.data.data
    },
    staleTime: 120_000,
  })

  const announcementsQ = useQuery({
    queryKey: ['admin-announcements-active'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: AdminAnnouncement[] }>('/api/v1/announcements/active')
      return res.data.data || []
    },
    refetchInterval: 300_000,
    staleTime: 120_000,
  })

  const overview = useQuery({
    queryKey: ['dash-overview'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ReportsOverview }>(
        '/api/v1/reports/overview',
      )
      return res.data.data
    },
    refetchInterval: 60_000,
  })

  const license = useQuery({
    queryKey: ['dash-license'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: LicenseInfo }>(
        '/api/v1/auth/license-info',
      )
      return res.data.data
    },
  })

  const openInvoices = useQuery({
    queryKey: ['dash-open-invoices'],
    queryFn: async () => {
      // /api/v1/invoices returns a paged { items, total, ... } envelope.
      // pay_state=open → only UNPAID invoices (paid_at IS NULL). Filtering by
      // status='issued' would wrongly include paid invoices, since a paid
      // invoice keeps status 'issued' (no 'paid' status — Ledger principle).
      const res = await apiClient.get<{ data: { items: InvoiceRow[] } }>(
        '/api/v1/invoices', { params: { pay_state: 'open', limit: 8 } },
      )
      return res.data.data?.items || []
    },
  })

  const billing = useQuery({
    queryKey: ['dash-billing-overview'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: BillingOverview }>('/api/v1/reports/billing-overview')
      return res.data.data
    },
  })

  const pspBalances = useQuery({
    queryKey: ['dash-psp-balances'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PSPBalances }>('/api/v1/reports/psp-balances')
      return res.data.data
    },
    staleTime: 300_000,
  })

  const openTickets = useQuery({
    queryKey: ['dash-open-tickets'],
    queryFn: async () => {
      // Same shape-tolerance as provisioning — accept both.
      const res = await apiClient.get<{
        data: TicketRow[] | { items?: TicketRow[] } | null
      }>(
        '/api/v1/tickets', { params: { status: 'active', limit: 20 } },
      )
      const d = res.data.data
      if (Array.isArray(d)) return d
      return d?.items || []
    },
  })

  const placedOrders = useQuery({
    queryKey: ['dash-placed-orders'],
    queryFn: async () => {
      // /api/v1/orders also returns a paged { items, total, ... } envelope
      const res = await apiClient.get<{ data: { items: OrderRow[] } }>(
        '/api/v1/orders', { params: { status: 'placed', limit: 8 } },
      )
      return res.data.data?.items || []
    },
  })

  const failedJobs = useQuery({
    queryKey: ['dash-failed-prov'],
    queryFn: async () => {
      // /api/v1/provisioning/jobs returns a flat array (not paged).
      // Be permissive — accept either shape so existing callers work
      // even if the backend later switches to a paged envelope.
      const res = await apiClient.get<{
        data: ProvJobRow[] | { items?: ProvJobRow[] } | null
      }>(
        '/api/v1/provisioning/jobs', { params: { status: 'failed', limit: 8 } },
      )
      const d = res.data.data
      if (Array.isArray(d)) return d
      return d?.items || []
    },
  })

  const salesQ = useQuery({
    queryKey: ['dash-sales'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: SalesRow[] }>('/api/v1/reports/sales', { params: { months: 12 } })
      return (res.data.data || []).map(r => ({ ...r, income: parseFloat(r.income) }))
    },
    staleTime: 300_000,
  })

  const mrrQ = useQuery({
    queryKey: ['dash-mrr'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: MRRRow[] }>('/api/v1/reports/mrr', { params: { months: 12 } })
      return (res.data.data || []).map(r => ({ ...r, mrr: parseFloat(r.mrr) }))
    },
    staleTime: 300_000,
  })

  const activityQ = useQuery({
    queryKey: ['dash-activity'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: AuditRow[] } }>('/api/v1/audit-log', {
        params: { limit: 15 },
      })
      return res.data.data?.items || []
    },
    refetchInterval: 30_000,
  })

  const overdueInvoices = useQuery({
    queryKey: ['dash-overdue-invoices'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: InvoiceRow[] } }>(
        '/api/v1/invoices', { params: { overdue: 'true', limit: 20 } },
      )
      return res.data.data?.items || []
    },
    refetchInterval: 300_000,
  })

  const creditExceeded = useQuery({
    queryKey: ['dash-credit-exceeded'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CreditLimitRow[] }>('/api/v1/reports/credit-limit-exceeded')
      return res.data.data || []
    },
    refetchInterval: 300_000,
  })

  const weekDue = useQuery({
    queryKey: ['dash-due-week'],
    queryFn: async () => {
      const d = new Date()
      d.setDate(d.getDate() + 7)
      const dueBefore = d.toISOString().slice(0, 10)
      const res = await apiClient.get<{ data: { items: DueSubRow[] } }>(
        '/api/v1/subscriptions',
        { params: { status: 'active', due_before: dueBefore, limit: 20 } },
      )
      return res.data.data?.items || []
    },
    refetchInterval: 300_000,
  })

  const o = overview.data
  const hasOverdue = (o?.overdue_invoices ?? 0) > 0
  const churnPct = parseFloat(o?.churn_rate_last_30_days || '0')

  const incomeDelta = pctDelta(parseFloat(o?.income_current_month || '0'), parseFloat(o?.income_last_month || '0'))
  const mrrSeries = mrrQ.data || []
  const mrrDelta = mrrSeries.length >= 2
    ? pctDelta(mrrSeries[mrrSeries.length - 1].mrr as unknown as number, mrrSeries[mrrSeries.length - 2].mrr as unknown as number)
    : undefined
  const hasSales = (salesQ.data || []).some(r => (r.income as unknown as number) > 0)
  const hasMrr = mrrSeries.some(r => (r.mrr as unknown as number) > 0)

  // PSP balances (Stripe / PayPal) — live account balances for the KPI strip.
  const curSym = (c?: string) => (c === 'EUR' || !c ? '€' : c)
  const pspTile = (bal: PSPBalance | null | undefined, err?: string) => {
    if (bal) {
      return {
        value: <>{bal.available} <span className="kpi-card-unit">{curSym(bal.currency)}</span></>,
        sub: `${t('dash.pspPending', 'ausstehend')}: ${bal.pending} ${curSym(bal.currency)}${bal.test_mode ? ' · TEST' : ''}`,
      }
    }
    return {
      value: <span className="kpi-card-value--text">{pspBalances.isLoading ? '…' : '—'}</span>,
      sub: err ? t('dash.pspUnavailable', 'nicht verfügbar') : t('dash.pspNotConfigured', 'nicht konfiguriert'),
    }
  }
  const stripeTile = pspTile(pspBalances.data?.stripe, pspBalances.data?.stripe_error)
  const paypalTile = pspTile(pspBalances.data?.paypal, pspBalances.data?.paypal_error)

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    { title: '#', dataIndex: 'invoice_no', key: 'no', width: 130,
      render: (v, r) => <Link to={`/invoices/${r.id}`}>{v || `#${r.id}`}</Link> },
    { title: t('dash.invCustomer', 'Kunde'), key: 'cust', width: 140,
      render: (_, r: InvoiceRow) => (
        <Link to={`/customers/${r.customer_id}`}>
          {r.recipient_company || r.recipient_name || `#${r.customer_id}`}
        </Link>
      ) },
    { title: t('dash.invDue', 'Fällig'), dataIndex: 'due_date', key: 'due', width: 110,
      render: (v?: string) => {
        if (!v) return '—'
        const overdue = new Date(v).getTime() < Date.now()
        return overdue
          ? <Tag color="error">{new Date(v).toLocaleDateString('de-DE')}</Tag>
          : new Date(v).toLocaleDateString('de-DE')
      } },
    { title: t('dash.invAmount', 'Betrag'), key: 'amt', align: 'right' as const,
      render: (_, r) => `${r.total_gross} ${r.currency}` },
  ]

  const ticketColumns: ColumnsType<TicketRow> = [
    { title: '#', dataIndex: 'ticket_no', key: 'no', width: 110,
      render: (v, r) => <Link to={`/tickets/${r.id}`}>{v}</Link> },
    { title: t('dash.ticketSubj', 'Betreff'), dataIndex: 'subject', key: 'subj' },
    { title: t('dash.ticketCustomer', 'Kunde'), key: 'cust', width: 140,
      render: (_, r) => r.customer_id
        ? <Link to={`/customers/${r.customer_id}`}>{r.customer_name || `#${r.customer_id}`}</Link>
        : '—' },
    { title: t('dash.ticketPrio', 'Priorität'), dataIndex: 'priority', key: 'prio',
      width: 100, render: (v: string) => <Tag color={({ low: 'default', normal: 'blue', high: 'orange', urgent: 'red' } as Record<string,string>)[v] || 'default'}>{{ low: 'Niedrig', normal: 'Normal', high: 'Hoch', urgent: 'Dringend' }[v as 'low'|'normal'|'high'|'urgent'] || v}</Tag> },
    { title: t('dash.ticketUpdated', 'Aktualisiert'), dataIndex: 'updated_at',
      key: 'upd', width: 150,
      render: (v: string) => new Date(v).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) },
  ]

  const orderColumns: ColumnsType<OrderRow> = [
    { title: '#', dataIndex: 'order_no', key: 'no', width: 130,
      render: (v, r) => <Link to={`/orders/${r.id}`}>{v || `#${r.id}`}</Link> },
    { title: t('dash.orderCustomer', 'Kunde'), key: 'cust', width: 150,
      render: (_, r) => r.customer_id
        ? <Link to={`/customers/${r.customer_id}`}>{r.customer_name || `#${r.customer_id}`}</Link>
        : r.customer_email || '—' },
    { title: t('dash.orderAmount', 'Brutto'), key: 'amt', align: 'right' as const,
      render: (_, r) => `${r.total_gross} ${r.currency}` },
    { title: t('dash.orderPlaced', 'Aufgegeben'), dataIndex: 'placed_at',
      key: 'placed', width: 150,
      render: (v?: string) => v ? new Date(v).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—' },
  ]

  const jobColumns: ColumnsType<ProvJobRow> = [
    { title: t('dash.jobAdapter', 'Adapter'), dataIndex: 'adapter', key: 'mod', width: 120 },
    { title: t('dash.jobAction', 'Aktion'), dataIndex: 'action', key: 'act', width: 110 },
    { title: t('dash.jobSub', 'Sub'), dataIndex: 'subscription_id', key: 'sub', width: 80,
      render: (v?: number) => v ? `#${v}` : '—' },
    { title: t('dash.jobAttempt', 'Versuch'), key: 'att', width: 90,
      render: (_, r) => `${r.attempts}/${r.max_attempts}` },
    { title: t('dash.jobError', 'Fehler'), dataIndex: 'last_error', key: 'err',
      render: (v?: string) => v
        ? <Typography.Text type="danger" className="text-xs">{v}</Typography.Text>
        : '—' },
  ]

  return (
    <>
      <PageHeader
        icon={<ThunderboltOutlined />}
        title={t('dashboard.title', 'Dashboard')}
        subtitle={t('dashboard.subtitle',
          'Live-Übersicht über Umsatz, offene Vorgänge und Systemstatus.')}
      />

      {/* Admin announcements */}
      {(announcementsQ.data || []).length > 0 && (
        <Space direction="vertical" className="w-full mb-16">
          {(announcementsQ.data || []).map(ann => (
            <Alert key={ann.id}
              type={ann.severity === 'critical' ? 'error' : ann.severity === 'warning' ? 'warning' : ann.severity === 'success' ? 'success' : 'info'}
              message={ann.title}
              description={ann.body}
              showIcon
              banner
            />
          ))}
        </Space>
      )}

      {/* KPI strip — top metrics */}
      <div className="kpi-card-grid mb-16">
        <KpiCard
          icon={<DollarOutlined />} accent="#6366F1"
          label={t('dash.mrr', 'MRR')}
          value={<>{o?.mrr ?? '0.00'} <span className="kpi-card-unit">€</span></>}
          sub={t('dash.mrrSub', 'Monthly Recurring Revenue')}
          delta={mrrDelta} deltaGood="up"
          onClick={() => navigate('/subscriptions')}
          title={t('dash.gotoSubs', 'Zu den Verträgen')}
        />
        <KpiCard
          icon={<CloudServerOutlined />} accent="#2563EB"
          label={t('dash.activeSubs', 'Aktive Verträge')}
          value={o?.active_subscriptions ?? 0}
          sub={`${t('dash.churn', 'Churn (30d)')}: ${churnPct.toFixed(1)}%`}
          onClick={() => navigate('/subscriptions')}
          title={t('dash.gotoSubs', 'Zu den Verträgen')}
        />
        <KpiCard
          icon={<FileTextOutlined />} accent={hasOverdue ? '#EF4444' : '#F59E0B'}
          label={t('dash.openInvoices', 'Offene Rechnungen')}
          value={o?.open_invoices ?? 0}
          sub={hasOverdue
            ? `${o!.overdue_invoices} überfällig · Σ ${o!.overdue_invoices_total} €`
            : `Σ ${o?.open_invoices_total ?? '0.00'} €`}
          onClick={() => navigate(hasOverdue ? '/invoices?pay_state=overdue' : '/invoices?pay_state=open')}
          title={hasOverdue ? t('dash.gotoOverdue', 'Zu den überfälligen Rechnungen') : t('dash.gotoOpen', 'Zu den offenen Rechnungen')}
        />
        <KpiCard
          icon={<CheckCircleOutlined />} accent="#10B981"
          label={t('dash.incomeMonth', 'Einnahmen lfd. Monat')}
          value={<>{o?.income_current_month ?? '0.00'} <span className="kpi-card-unit">€</span></>}
          sub={`${t('dash.incomeLast', 'Vormonat')}: ${o?.income_last_month ?? '0.00'} €`}
          delta={incomeDelta} deltaGood="up"
          onClick={() => navigate('/invoices?pay_state=paid')}
          title={t('dash.gotoPaid', 'Zu den bezahlten Rechnungen')}
        />
        <KpiCard
          icon={<CreditCardOutlined />} accent="#635BFF"
          label={t('dash.stripeBalance', 'Stripe-Kontostand')}
          value={stripeTile.value}
          sub={stripeTile.sub}
        />
        <KpiCard
          icon={<DollarOutlined />} accent="#003087"
          label={t('dash.paypalBalance', 'PayPal-Kontostand')}
          value={paypalTile.value}
          sub={paypalTile.sub}
        />
        <KpiCard
          icon={<UserAddOutlined />} accent="#8B5CF6"
          label={t('dash.newCustomers', 'Neue Kunden')}
          value={o?.new_customers_this_month ?? 0}
          sub={t('dash.thisMonth', 'lfd. Monat')}
          onClick={() => navigate('/customers')}
          title={t('dash.gotoCustomers', 'Zu den Kunden')}
        />
        <KpiCard
          icon={license.data?.valid ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          accent={license.data?.valid ? '#14B8A6' : '#EF4444'}
          label={t('dash.license', 'Lizenz')}
          value={<span className="kpi-card-value--text">{license.data?.valid
            ? (license.data.product || t('dash.licenseOk', 'gültig'))
            : (license.data?.type === 'trial' ? t('dash.licenseTrial', 'Trial') : t('dash.licenseInvalid', 'ungültig'))}</span>}
          sub={license.data?.grace_period ? t('dash.gracePeriod', 'Grace-Periode') : t('dash.licenseActive', 'aktiv')}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
        {dashEdit ? (
          <>
            <Button size="small" onClick={resetDashLayout}>{t('dash.layoutReset', 'Standard')}</Button>
            <Button size="small" type="primary" onClick={saveDashLayout}>{t('dash.layoutSave', 'Layout speichern')}</Button>
          </>
        ) : (
          <Button size="small" icon={<HolderOutlined />} onClick={() => setDashEdit(true)}>{t('dash.layoutEdit', 'Dashboard anpassen')}</Button>
        )}
      </div>
      <ResponsiveGrid
        className={dashEdit ? 'dash-rgl dash-editing' : 'dash-rgl'}
        layouts={dashLayouts}
        breakpoints={DASH_BREAKPOINTS}
        cols={DASH_COLS}
        rowHeight={30}
        margin={[16, 16]}
        isDraggable={dashEdit}
        isResizable={dashEdit}
        draggableHandle=".dash-drag-handle"
        onLayoutChange={onDashLayoutChange}
      >

      <div key="servermgmt" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card mb-16" size="small"
          title={<span className="chart-card-title"><CloudServerOutlined /> {t('dash.serverMgmt', 'Server-Management')}</span>}
          extra={<Link to="/server-management">{t('common.overview', 'Übersicht')}</Link>}>
          {!serverMgmtQ.data ? (
            <Typography.Text type="secondary">{serverMgmtQ.isLoading ? t('common.loading', 'Lädt…') : '—'}</Typography.Text>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14 }}>
                <div onClick={() => navigate(serverMgmtQ.data.totals.reboot ? '/server-management?tab=hosts&f=reboot' : '/server-management')} style={{ cursor: 'pointer' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: serverMgmtQ.data.totals.reboot ? '#EF4444' : '#10B981', lineHeight: 1.1 }}>{serverMgmtQ.data.totals.reboot}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.smReboot', 'Reboot ausstehend')}</div>
                </div>
                <div onClick={() => navigate('/server-management')} style={{ cursor: 'pointer' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: serverMgmtQ.data.totals.updates ? '#F59E0B' : '#10B981', lineHeight: 1.1 }}>{serverMgmtQ.data.totals.updates}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.smUpdates', 'Hosts mit Updates')}{serverMgmtQ.data.totals.security ? ` · ${serverMgmtQ.data.totals.security} Security` : ''}</div>
                </div>
                <div onClick={() => navigate('/server-management')} style={{ cursor: 'pointer' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: serverMgmtQ.data.totals.problem ? '#EF4444' : '#10B981', lineHeight: 1.1 }}>{serverMgmtQ.data.totals.problem}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.smProblem', 'Nicht erreichbar')}</div>
                </div>
                <div onClick={() => navigate('/domains')} style={{ cursor: 'pointer' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: serverMgmtQ.data.totals.domains_expiring ? '#F59E0B' : '#10B981', lineHeight: 1.1 }}>{serverMgmtQ.data.totals.domains_expiring}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.smDomains', 'Domains < 30 Tage')}</div>
                </div>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#6366F1', lineHeight: 1.1 }}>{serverMgmtQ.data.totals.hosts}</div>
                  <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.smHosts', 'Hosts gesamt')}</div>
                </div>
              </div>
              <div className="dashboard-grid" style={{ gap: 16 }}>
                <div>
                  {serverMgmtQ.data.reboot_required.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Typography.Text strong style={{ fontSize: 12 }}>{t('dash.smReboot', 'Reboot ausstehend')}</Typography.Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                        {serverMgmtQ.data.reboot_required.map(h => (
                          <Link key={h.id} to={`/server-management/hosts/${h.id}`}><Tag color="red">{h.name}</Tag></Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {serverMgmtQ.data.updates_pending.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <Typography.Text strong style={{ fontSize: 12 }}>{t('dash.smUpdates', 'Updates verfügbar')}</Typography.Text>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                        {serverMgmtQ.data.updates_pending.slice(0, 8).map(h => (
                          <div key={h.id} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                            <Link to={`/server-management/hosts/${h.id}`}>{h.name}</Link>
                            <span style={{ color: '#64748B', whiteSpace: 'nowrap' }}>{h.updates_available} Updates{h.security_updates ? ` · ${h.security_updates} Sec` : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {serverMgmtQ.data.problem.length > 0 && (
                    <div>
                      <Typography.Text strong style={{ fontSize: 12 }}>{t('dash.smProblem', 'Nicht erreichbar')}</Typography.Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                        {serverMgmtQ.data.problem.map(h => (
                          <Link key={h.id} to={`/server-management/hosts/${h.id}`}><Tag color="volcano">{h.name} · {h.status}</Tag></Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {serverMgmtQ.data.totals.reboot === 0 && serverMgmtQ.data.totals.updates === 0 && serverMgmtQ.data.totals.problem === 0 && (
                    <Typography.Text type="secondary">{t('dash.smAllOk', 'Alle Hosts aktuell & erreichbar')}</Typography.Text>
                  )}
                </div>
                <div>
                  <Typography.Text strong style={{ fontSize: 12 }}>{t('dash.smDomainsList', 'Auslaufende Domains (30 Tage)')}</Typography.Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
                    {serverMgmtQ.data.expiring_domains.slice(0, 10).map(d => (
                      <div key={d.id} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <Link to={`/domains?q=${encodeURIComponent(d.name)}`}>{d.name}</Link>
                        <span style={{ color: '#64748B', whiteSpace: 'nowrap' }}>
                          {d.expires_at ? new Date(d.expires_at).toLocaleDateString('de-DE') : '—'}
                          {!d.auto_renew && <Tag color="orange" style={{ marginLeft: 6 }}>{t('dash.smNoAutoRenew', 'kein Auto-Renew')}</Tag>}
                        </span>
                      </div>
                    ))}
                    {serverMgmtQ.data.expiring_domains.length === 0 && (
                      <Typography.Text type="secondary">{t('dash.smNoDomains', 'Keine Domains laufen bald aus')}</Typography.Text>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      <div key="billing" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card" size="small"
          title={<span className="chart-card-title"><FileTextOutlined /> {t('dash.billingTitle', 'Rechnungen & Mahnstufen')}</span>}>
  <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14 }}>
            <div onClick={() => navigate('/invoices?pay_state=open')} style={{ cursor: 'pointer' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#F59E0B', lineHeight: 1.1 }}>{billing.data?.due_count ?? 0}</div>
              <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.dueOpen', 'Fällig (offen)')} · Σ {billing.data?.due_sum ?? '0.00'} €</div>
            </div>
            <div onClick={() => navigate('/invoices?pay_state=overdue')} style={{ cursor: 'pointer' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#EF4444', lineHeight: 1.1 }}>{billing.data?.overdue_count ?? 0}</div>
              <div style={{ color: '#64748B', fontSize: 13 }}>{t('dash.overdue', 'Überfällig')} · Σ {billing.data?.overdue_sum ?? '0.00'} €</div>
            </div>
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            {t('dash.dunningBreakdown', 'Offene Rechnungen nach Mahnstufe')}
          </Typography.Text>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(billing.data?.dunning || []).map(b => {
              const actionLabel = b.action === 'suspend' ? t('dash.dunSuspend', 'Sperre')
                : b.action === 'terminate' ? t('dash.dunTerminate', 'Kündigung')
                : b.action === 'notify' ? t('dash.dunNotify', 'Hinweis') : ''
              const color = b.level === 0 ? 'default' : b.level >= 3 ? 'red' : b.level === 2 ? 'orange' : 'gold'
              const label = b.level === 0
                ? t('dash.dunNone', 'Noch nicht angemahnt')
                : `${t('dash.dunLevel', 'Mahnstufe')} ${b.level}${actionLabel ? ' · ' + actionLabel : ''}`
              return (
                <Tag key={b.level} color={color} style={{ padding: '4px 10px', fontSize: 13 }}>
                  {label}: <b>{b.count}</b> · Σ {b.sum} €
                </Tag>
              )
            })}
            {(billing.data?.dunning?.length ?? 0) === 0 && (
              <Typography.Text type="secondary">{t('dash.noOpen', 'Keine offenen Rechnungen')}</Typography.Text>
            )}
          </div>
        </Card>
      </div>

      <div key="revenue" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card chart-card" size="small"
          title={<span className="chart-card-title"><DollarOutlined /> {t('dash.revenueChart', 'Einnahmen (12 Monate)')}</span>}>
          <div className="chart-canvas">
            {hasSales ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(salesQ.data || []).map(r => ({ ...r, month: fmtMonth(r.month) }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366F1" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#818CF8" stopOpacity={0.65} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#EEF2F7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtEur} tick={{ fontSize: 11, fill: '#94A3B8' }} width={42} axisLine={false} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(99,102,241,0.06)' }}
                    formatter={(v: unknown) => [`${(v as number).toFixed(2)} €`, 'Einnahmen']}
                    contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 8px 24px rgba(15,23,42,0.10)', fontSize: 12 }}
                    labelStyle={{ fontWeight: 700, color: '#0F172A' }}
                  />
                  <Bar dataKey="income" fill="url(#revGrad)" radius={[6, 6, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
            ) : <ChartEmpty loading={salesQ.isLoading} />}
          </div>
        </Card>
      </div>

      <div key="mrr" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card chart-card" size="small"
          title={<span className="chart-card-title"><CloudServerOutlined /> {t('dash.mrrChart', 'MRR-Verlauf (12 Monate)')}</span>}>
          <div className="chart-canvas">
            {hasMrr ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mrrSeries.map(r => ({ ...r, month: fmtMonth(r.month) }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#6366F1" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#EEF2F7" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtEur} tick={{ fontSize: 11, fill: '#94A3B8' }} width={42} axisLine={false} tickLine={false} />
                  <Tooltip
                    formatter={(v: unknown) => [`${(v as number).toFixed(2)} €`, 'MRR']}
                    contentStyle={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 8px 24px rgba(15,23,42,0.10)', fontSize: 12 }}
                    labelStyle={{ fontWeight: 700, color: '#0F172A' }}
                  />
                  <Area type="monotone" dataKey="mrr" stroke="#6366F1" strokeWidth={2.5}
                    fill="url(#mrrGrad)" dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <ChartEmpty loading={mrrQ.isLoading} />}
          </div>
        </Card>
      </div>

      <div key="openinvoices" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card" size="small"
          title={<Space><FileTextOutlined /> {t('dash.openInvList', 'Offene Rechnungen')}</Space>}
          extra={<Link to="/invoices">{t('common.all', 'Alle')}</Link>}>
          <DataTable<InvoiceRow>
            rowKey="id"
            dataSource={openInvoices.data || []}
            columns={invoiceColumns}
            renderMobileCard={(r: InvoiceRow) => (
              <div className="mob-card">
                <div className="flex-between mb-6">
                  <Link to={`/invoices/${r.id}`}><strong>{r.invoice_no || `#${r.id}`}</strong></Link>
                  <span>{r.total_gross} {r.currency}</span>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <Link to={`/customers/${r.customer_id}`}>{r.recipient_company || r.recipient_name || `#${r.customer_id}`}</Link>
                  {r.due_date && <Tag color={new Date(r.due_date).getTime() < Date.now() ? 'error' : undefined}>{new Date(r.due_date).toLocaleDateString('de-DE')}</Tag>}
                </div>
              </div>
            )}
          />
        </Card>
      </div>

      <div key="opentickets" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card" size="small"
          title={<Space><CommentOutlined /> {t('dash.openTicketsList', 'Offene Tickets')}</Space>}
          extra={<Link to="/tickets">{t('common.all', 'Alle')}</Link>}>
          <DataTable<TicketRow>
            rowKey="id"
            dataSource={openTickets.data || []}
            columns={ticketColumns}
            renderMobileCard={(r: TicketRow) => (
              <div className="mob-card">
                <div className="flex-between mb-6">
                  <Link to={`/tickets/${r.id}`}><strong>{r.ticket_no}</strong></Link>
                  <Tag color={({ low: 'default', normal: 'blue', high: 'orange', urgent: 'red' } as Record<string,string>)[r.priority] || 'default'}>
                    {{ low: 'Niedrig', normal: 'Normal', high: 'Hoch', urgent: 'Dringend' }[r.priority as 'low'|'normal'|'high'|'urgent'] || r.priority}
                  </Tag>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <span>{r.subject}</span>
                  {r.customer_id && <Link to={`/customers/${r.customer_id}`}>{r.customer_name || `#${r.customer_id}`}</Link>}
                </div>
              </div>
            )}
          />
        </Card>
      </div>

      <div key="orders" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card" size="small"
          title={<Space><ShoppingCartOutlined /> {t('dash.placedOrders', 'Aktive Bestellungen')}</Space>}
          extra={<Link to="/orders">{t('common.all', 'Alle')}</Link>}>
          <DataTable<OrderRow>
            rowKey="id"
            dataSource={placedOrders.data || []}
            columns={orderColumns}
            renderMobileCard={(r: OrderRow) => (
              <div className="mob-card">
                <div className="flex-between mb-6">
                  <Link to={`/orders/${r.id}`}><strong>{r.order_no || `#${r.id}`}</strong></Link>
                  <span>{r.total_gross} {r.currency}</span>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  {r.customer_id
                    ? <Link to={`/customers/${r.customer_id}`}>{r.customer_name || `#${r.customer_id}`}</Link>
                    : <span>{r.customer_email || '—'}</span>}
                  {r.placed_at && <span>{new Date(r.placed_at).toLocaleDateString('de-DE')}</span>}
                </div>
              </div>
            )}
          />
        </Card>
      </div>

      <div key="failedjobs" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card" size="small"
          title={<Space><AlertOutlined /> {t('dash.failedJobs', 'Fehlgeschlagene Provisioning-Jobs')}</Space>}
          extra={<Link to="/provisioning-jobs">{t('common.all', 'Alle')}</Link>}>
          <DataTable<ProvJobRow>
            rowKey="id"
            dataSource={failedJobs.data || []}
            columns={jobColumns}
            renderMobileCard={(r: ProvJobRow) => (
              <div className="mob-card mob-card--urgent">
                <div className="flex-between mb-6">
                  <strong>{r.adapter} · {r.action}</strong>
                  <Tag color="error">{r.attempts}/{r.max_attempts}</Tag>
                </div>
                {r.last_error && (
                  <Typography.Text type="danger" className="text-xs">{r.last_error}</Typography.Text>
                )}
              </div>
            )}
          />
        </Card>
      </div>

      <div key="renewals" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
      {/* Renewals due this week */}
      <Card className="content-card mb-16" size="small"
        title={<Space><SyncOutlined /> {t('dash.dueWeek', 'Verlängerungen — nächste 7 Tage')}</Space>}
        extra={<Link to="/subscriptions?status=active">{t('common.all', 'Alle')}</Link>}>
        <DataTable<DueSubRow>
          rowKey="id"
          dataSource={weekDue.data || []}
          loading={weekDue.isLoading}
          columns={[
            { title: t('dash.dueDate', 'Fällig'), dataIndex: 'next_due_date', key: 'due', width: 110,
              render: (v?: string | null) => v
                ? <Tag color={new Date(v) <= new Date() ? 'red' : 'blue'}>
                    {new Date(v).toLocaleDateString('de-DE')}
                  </Tag>
                : '—' },
            { title: t('dash.customer', 'Kunde'), key: 'cust',
              render: (_: unknown, r: DueSubRow) => (
                <Link to={`/customers/${r.customer_id}`}>{r.customer_name || r.customer_no}</Link>
              ) },
            { title: t('dash.product', 'Produkt'), dataIndex: 'product_name', key: 'prod' },
            { title: t('dash.cycle', 'Rhythmus'), dataIndex: 'billing_cycle', key: 'cycle', width: 100,
              render: (v: string) => <Tag>{t(`billingCycle.${v}`, v)}</Tag> },
            { title: '', key: 'sub', width: 80, align: 'right' as const,
              render: (_: unknown, r: DueSubRow) => (
                <Link to={`/subscriptions/${r.id}`}>Detail</Link>
              ) },
          ]}
          renderMobileCard={(r: DueSubRow) => (
            <div className="mob-card">
              <div className="flex-between mb-6">
                <Link to={`/customers/${r.customer_id}`}><strong>{r.customer_name || r.customer_no}</strong></Link>
                {r.next_due_date && (
                  <Tag color={new Date(r.next_due_date) <= new Date() ? 'red' : 'blue'}>
                    {new Date(r.next_due_date).toLocaleDateString('de-DE')}
                  </Tag>
                )}
              </div>
              <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                <span>{r.product_name} · <Tag style={{ fontSize: 11 }}>{r.billing_cycle}</Tag></span>
                <Link to={`/subscriptions/${r.id}`}>Detail</Link>
              </div>
            </div>
          )}
        />
      </Card>
      </div>

      {/* Overdue Invoices */}
      {(overdueInvoices.data || []).length > 0 && (
        <div key="overdue" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card mb-16" size="small"
          title={<Space><ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> {t('dash.overdueInvoices', 'Überfällige Rechnungen')}</Space>}
          extra={<Link to="/invoices?overdue=true">{t('common.all', 'Alle')}</Link>}>
          <DataTable<InvoiceRow>
            rowKey="id"
            dataSource={overdueInvoices.data || []}
            loading={overdueInvoices.isLoading}
            columns={[
              { title: '#', dataIndex: 'invoice_no', key: 'no', width: 130,
                render: (v: string, r: InvoiceRow) => <Link to={`/invoices/${r.id}`}>{v || `#${r.id}`}</Link> },
              { title: t('dash.invCustomer', 'Kunde'), key: 'cust',
                render: (_: unknown, r: InvoiceRow) => (
                  <Link to={`/customers/${r.customer_id}`}>
                    {r.recipient_company || r.recipient_name || `#${r.customer_id}`}
                  </Link>
                ) },
              { title: t('dash.invDue', 'Fällig'), dataIndex: 'due_date', key: 'due', width: 110,
                render: (v?: string) => v
                  ? <Tag color="error">{new Date(v).toLocaleDateString('de-DE')}</Tag>
                  : '—' },
              { title: t('dash.invAmount', 'Betrag'), key: 'amt', align: 'right' as const,
                render: (_: unknown, r: InvoiceRow) => `${r.total_gross} ${r.currency}` },
            ]}
            renderMobileCard={(r: InvoiceRow) => (
              <div className="mob-card mob-card--urgent">
                <div className="flex-between mb-6">
                  <Link to={`/invoices/${r.id}`}><strong>{r.invoice_no || `#${r.id}`}</strong></Link>
                  <Tag color="error">{r.due_date ? new Date(r.due_date).toLocaleDateString('de-DE') : '—'}</Tag>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <Link to={`/customers/${r.customer_id}`}>{r.recipient_company || r.recipient_name || `#${r.customer_id}`}</Link>
                  <strong>{r.total_gross} {r.currency}</strong>
                </div>
              </div>
            )}
          />
        </Card>
        </div>
      )}

      {/* Credit Limit Exceeded */}
      {(creditExceeded.data || []).length > 0 && (
        <div key="creditlimit" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
        <Card className="content-card mb-16" size="small"
          title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} /> {t('dash.creditExceeded', 'Kreditlimit überschritten')}</Space>}
          extra={<Link to="/customers">{t('common.all', 'Alle')}</Link>}>
          <DataTable<CreditLimitRow>
            rowKey="customer_id"
            dataSource={creditExceeded.data || []}
            loading={creditExceeded.isLoading}
            columns={[
              { title: t('customer.no', 'Kundennr.'), dataIndex: 'customer_no', key: 'no', width: 110,
                render: (v: string, r: CreditLimitRow) => <Link to={`/customers/${r.customer_id}`}>{v}</Link> },
              { title: t('customer.name', 'Name'), dataIndex: 'customer_name', key: 'name' },
              { title: t('dash.balance', 'Saldo'), key: 'bal', align: 'right' as const,
                render: (_: unknown, r: CreditLimitRow) => <Tag color="error">{r.balance} {r.currency}</Tag> },
              { title: t('dash.creditLimit', 'Limit'), key: 'limit', align: 'right' as const,
                render: (_: unknown, r: CreditLimitRow) => `${r.credit_limit} ${r.currency}` },
              { title: t('dash.excess', 'Überzug'), key: 'exc', align: 'right' as const,
                render: (_: unknown, r: CreditLimitRow) => <Typography.Text type="danger">+{r.excess_amount} {r.currency}</Typography.Text> },
            ]}
            renderMobileCard={(r: CreditLimitRow) => (
              <div className="mob-card mob-card--urgent">
                <div className="flex-between mb-6">
                  <Link to={`/customers/${r.customer_id}`}><strong>{r.customer_name}</strong></Link>
                  <Tag color="error">{r.balance} {r.currency}</Tag>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <span>{r.customer_no} · Limit: {r.credit_limit} {r.currency}</span>
                  <Typography.Text type="danger">+{r.excess_amount} {r.currency}</Typography.Text>
                </div>
              </div>
            )}
          />
        </Card>
        </div>
      )}

      {/* Recent Activity Timeline */}
      <div key="activity" className="dash-tile"><span className="dash-drag-handle"><HolderOutlined /></span>
      <Card className="content-card" size="small"
        title={<Space><HistoryOutlined /> {t('dash.recentActivity', 'Letzte Aktivitäten')}</Space>}
        extra={<Link to="/audit-log">{t('common.all', 'Alle')}</Link>}>
        <Timeline
          items={(activityQ.data || []).map(e => ({
            key: e.id,
            children: (
              <div className="flex-between">
                <div>
                  <Typography.Text strong className="mr-6">{e.actor}</Typography.Text>
                  <Tag color={auditActionColor(e.action)}>{e.action}</Tag>
                  {e.subject_kind && (
                    <Typography.Text type="secondary" className="text-xs ml-4">
                      {e.subject_kind}{e.subject ? ` #${e.subject}` : ''}
                    </Typography.Text>
                  )}
                </div>
                <Typography.Text type="secondary" className="text-xs">
                  {new Date(e.created_at).toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Typography.Text>
              </div>
            ),
          }))}
        />
        {(activityQ.data || []).length === 0 && (
          <Typography.Text type="secondary">{t('dash.noActivity', 'Noch keine Einträge.')}</Typography.Text>
        )}
      </Card>
      </div>

      </ResponsiveGrid>
    </>
  )
}

function auditActionColor(action: string): string {
  switch (action) {
    case 'create': return 'success'
    case 'delete': return 'error'
    case 'login':  return 'blue'
    case 'logout': return 'default'
    case 'update': case 'edit': return 'processing'
    case 'suspend': case 'deactivate': return 'warning'
    case 'activate': case 'unsuspend': return 'success'
    default: return 'default'
  }
}
